import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./WhatsAppButton.css";

// Noras WhatsApp, +46 73 550 71 23. Samma nummer som leadutskicken går från, så en
// gäst som skriver här hamnar i samma tråd och samma robot som alla andra vägar in.
// Gustavs egna nummer ska INTE stå här: då får han tillbaka varje fråga för hand.
const WA_NUMBER = "46735507123";

// Ren länk, inget inbäddat skript och ingen tredjepart. Därför krävs inget
// marknadsföringssamtycke, till skillnad från röstwidgeten den ersätter, som bara
// laddades för besökare som tackade ja till kakor och därför knappt syntes.
const WhatsAppButton = () => {
  const { t, i18n } = useTranslation();
  // Alla har inte WhatsApp (Gustav 7 okt). Knappen öppnar därför två val: WhatsApp till
  // Nora, eller kontaktformuläret, som går in i samma CRM och besvaras på mejl.
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const lang = (i18n?.language || "en").split("-")[0];
  const openLabel = t("whatsapp.open", "Contact us");
  const label = t("whatsapp.label", "Chat with us on WhatsApp");
  const prefill = t("whatsapp.prefill", "Hi! I'd like to know more about a winter trip to Lapland.");
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(prefill)}`;

  // Facebooks, Instagrams och Messengers inbyggda webbläsare lämnar inte över wa.me-länken
  // till WhatsApp-appen. Gästen hamnar på "Looks like you don't have WhatsApp installed"
  // fast appen finns (sett 6 okt). De flesta kommer just därifrån, från en annons eller en
  // profil. Där anropas appen direkt i stället. Händer inget inom två sekunder går vi till
  // den vanliga länken, så ingen blir stående på en knapp som inte gör något.
  const openInApp = (e) => {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const inApp = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|LinkedInApp/i.test(ua);
    if (!inApp) return;
    e.preventDefault();
    const text = encodeURIComponent(prefill);
    const direct = /Android/i.test(ua)
      ? `intent://send?phone=${WA_NUMBER}&text=${text}#Intent;scheme=whatsapp;package=com.whatsapp;S.browser_fallback_url=${encodeURIComponent(href)};end`
      : `whatsapp://send?phone=${WA_NUMBER}&text=${text}`;
    const fallback = setTimeout(() => {
      if (!document.hidden) window.location.href = href;
    }, 2000);
    const stop = () => { if (document.hidden) clearTimeout(fallback); };
    document.addEventListener("visibilitychange", stop, { once: true });
    window.location.href = direct;
  };

  return (
    <div className="whatsapp-widget" ref={ref}>
      {open && (
        <div className="whatsapp-menu" role="menu">
          <a className="whatsapp-menu__item" role="menuitem" href={href} onClick={(e) => { openInApp(e); setOpen(false); }} target="_blank" rel="noopener noreferrer">
            <svg className="whatsapp-menu__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.38a9.86 9.86 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0 0 12.04 2m0 1.67c2.2 0 4.27.86 5.82 2.42a8.19 8.19 0 0 1 2.42 5.82c0 4.54-3.7 8.23-8.24 8.23a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.18 8.18 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23m-3.2 4.3c-.15 0-.4.06-.6.28-.21.22-.8.78-.8 1.9 0 1.12.82 2.2.93 2.36.12.15 1.6 2.44 3.87 3.42.54.23.96.37 1.29.48.54.17 1.04.15 1.43.09.43-.07 1.34-.55 1.53-1.08.19-.53.19-.98.13-1.08-.06-.09-.21-.15-.43-.26-.23-.11-1.34-.66-1.55-.74-.2-.07-.36-.11-.5.12-.16.22-.58.73-.71.88-.13.15-.26.17-.49.06-.22-.12-.95-.35-1.8-1.12a6.8 6.8 0 0 1-1.26-1.55c-.13-.23-.01-.35.1-.46.1-.1.22-.26.34-.4.11-.13.15-.22.22-.37.08-.15.04-.28-.02-.39-.06-.11-.5-1.22-.69-1.67-.18-.44-.36-.38-.5-.38z"
        />
      </svg>
            <span>{t("whatsapp.chat", "Chat on WhatsApp")}</span>
          </a>
          <Link className="whatsapp-menu__item" role="menuitem" to={`/${lang}/contact`} onClick={() => setOpen(false)}>
            <svg className="whatsapp-menu__icon whatsapp-menu__icon--mail" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M3.5 6.5h17v11h-17zM4 7l8 6.5L20 7" />
            </svg>
            <span>{t("whatsapp.email", "No WhatsApp? Email us")}</span>
          </Link>
        </div>
      )}
      <button type="button" className="whatsapp-button" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} aria-label={openLabel} title={openLabel}>
      <svg className="whatsapp-button__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.38a9.86 9.86 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0 0 12.04 2m0 1.67c2.2 0 4.27.86 5.82 2.42a8.19 8.19 0 0 1 2.42 5.82c0 4.54-3.7 8.23-8.24 8.23a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.18 8.18 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23m-3.2 4.3c-.15 0-.4.06-.6.28-.21.22-.8.78-.8 1.9 0 1.12.82 2.2.93 2.36.12.15 1.6 2.44 3.87 3.42.54.23.96.37 1.29.48.54.17 1.04.15 1.43.09.43-.07 1.34-.55 1.53-1.08.19-.53.19-.98.13-1.08-.06-.09-.21-.15-.43-.26-.23-.11-1.34-.66-1.55-.74-.2-.07-.36-.11-.5.12-.16.22-.58.73-.71.88-.13.15-.26.17-.49.06-.22-.12-.95-.35-1.8-1.12a6.8 6.8 0 0 1-1.26-1.55c-.13-.23-.01-.35.1-.46.1-.1.22-.26.34-.4.11-.13.15-.22.22-.37.08-.15.04-.28-.02-.39-.06-.11-.5-1.22-.69-1.67-.18-.44-.36-.38-.5-.38z"
        />
      </svg>
        <span className="whatsapp-button__label">{label}</span>
      </button>
    </div>
  );
};

export default WhatsAppButton;
